import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { SrIcon } from "@/components/ui/SrIcon";
import { useCustomers } from "@/hooks/use-customers";
import { useUpdateProjectCustomer } from "@/hooks/use-update-project-customer";

interface Props {
  projectId: string;
  currentCustomerId: string | null;
  currentCustomerName: string | null;
  /** VIB-757: project.updated_at brukes til optimistic locking. */
  projectUpdatedAt: string;
}

/**
 * Søkbart kunde-velger for prosjekt-administrasjon. Med 700+ kunder i DB
 * er native <select> ubrukelig — bruker Popover+Command for fritekst-søk
 * (samme mønster som TemplatePicker).
 *
 * Lagrer projects.customer_id direkte; invaliderer project-detail og
 * customers så Kundeinfo-kortet refresher med ny kunde.
 */
export function CustomerPicker({ projectId, currentCustomerId, currentCustomerName, projectUpdatedAt }: Props) {
  const { data: customers = [] } = useCustomers();
  const updateCustomer = useUpdateProjectCustomer();
  const [open, setOpen] = useState(false);
  const saving = updateCustomer.isPending;

  const handleSelect = (customerId: string) => {
    if (customerId === currentCustomerId) {
      setOpen(false);
      return;
    }
    updateCustomer.mutate(
      { projectId, customerId, expectedUpdatedAt: projectUpdatedAt },
      {
        onSettled: () => setOpen(false),
      },
    );
  };

  return (
    <div className="flex justify-between items-center gap-2 group cursor-pointer hover:bg-muted/40 px-2 py-1 rounded transition-colors">
      <span className="text-muted-foreground whitespace-nowrap">Kundenavn</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="flex items-center gap-2 hover:underline"
            disabled={saving}
          >
            <span className="font-medium text-right">
              {saving ? "Lagrer..." : currentCustomerName ?? "Velg kunde..."}
            </span>
            <SrIcon name="pencil" className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-80" align="end">
          <Command>
            <CommandInput placeholder="Søk i kunder..." className="h-9" />
            <CommandList className="max-h-72">
              <CommandEmpty>Ingen kunder matcher søket.</CommandEmpty>
              <CommandGroup heading={`${customers.length} kunder`}>
                {customers.map((c) => (
                  <CommandItem
                    key={c.id}
                    value={`${c.name} ${c.organization_number ?? ""}`}
                    onSelect={() => handleSelect(c.id)}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span className="text-xs truncate">{c.name}</span>
                      {c.organization_number && (
                        <span className="text-[10px] text-muted-foreground ml-2 shrink-0 tabular-nums">
                          {c.organization_number}
                        </span>
                      )}
                    </div>
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
